/**
 * StorageUsageBar — org-scoped storage meter.
 *
 * PLANS-1B copy pass. Same enforcement contract as before — reads live
 * numbers from `useUsageSnapshot()` so the bar and the server-side
 * gate can't drift.
 *
 * Copy states (post-PLANS-1 tier vocabulary):
 *   Starter <75%   — "142.3 GB of 500 GB used" (muted)
 *   Starter 75-89% — "142.3 GB of 500 GB used — you're getting close." (amber)
 *   Starter 90-99% — "More space is coming your way. Orbit adds 500 GB more." (orange)
 *   Orbit    90-99%— "More space is coming your way. Orbit Business is unlimited." (orange)
 *   100%           — "You've reached your Orbit storage limit. Your existing
 *                     content is safe. To upload more, free up space or grow
 *                     your plan." (two CTAs)
 *   Business       — "Unlimited — reasonable use" (no cap copy)
 *
 * Analytics:
 *   storage_limit_reached  — fires once per mount when usage hits 100%
 *   storage_upgrade_viewed — fires once per mount when the nudge shows
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  formatBytes,
  useUsageSnapshot,
  type StorageBand,
} from '@/hooks/useUsageSnapshot';
import { track } from '@/lib/analytics';
import { UpgradeNudgeCard } from '@/components/shared/UpgradeNudgeCard';
import { PLAN_LABELS, type SubscriptionPlan } from '@/services/billing';

export interface StorageUsageBarProps {
  /** Compact single-row variant for the Today dashboard. */
  compact?: boolean;
  /** Extra wrapper classes. */
  className?: string;
  /**
   * Override the routing target for the "Manage storage" / "See
   * plans" CTAs. Defaults to Settings → Business (billing tab).
   */
  billingHref?: string;
  /** Storage management target (usually deliveries / galleries). */
  manageHref?: string;
}

export function StorageUsageBar({
  compact = false,
  className,
  billingHref = '/settings?tab=billing',
  manageHref = '/deliveries',
}: StorageUsageBarProps) {
  const navigate = useNavigate();
  const { plan, storage, isLoading } = useUsageSnapshot();

  // Fire analytics events once per mount, guarded so re-renders don't
  // spam the pipeline. Refs so the strict-mode double-invoke doesn't
  // double-fire in dev.
  const limitReachedFiredRef = useRef(false);
  const upgradeViewedFiredRef = useRef(false);

  useEffect(() => {
    if (storage.isOverLimit && !limitReachedFiredRef.current) {
      track('storage_limit_reached', {
        plan,
        used_bytes: storage.usedBytes,
        cap_bytes: storage.capBytes,
      });
      limitReachedFiredRef.current = true;
    }
    if (storage.isAtLimit && !upgradeViewedFiredRef.current) {
      track('storage_upgrade_viewed', {
        plan,
        percent: storage.percent,
        reason: 'storage',
      });
      upgradeViewedFiredRef.current = true;
    }
  }, [
    storage.isOverLimit,
    storage.isAtLimit,
    storage.usedBytes,
    storage.capBytes,
    storage.percent,
    plan,
  ]);

  if (isLoading) {
    return (
      <div
        className={cn(
          'rounded-2xl border border-foreground/10 bg-background/40 p-3 sm:p-4 animate-pulse',
          className,
        )}
      >
        <div className="h-3 w-1/3 rounded bg-foreground/10" />
        <div className="mt-3 h-2 w-full rounded-full bg-foreground/5" />
      </div>
    );
  }

  const barColor = storage.isUnlimited
    ? 'bg-orbit-orange/60'
    : storage.isAtLimit
    ? 'bg-red-500'
    : storage.isNearLimit
    ? 'bg-amber-500'
    : 'bg-orbit-orange';

  const usedLabel = formatBytes(storage.usedBytes);
  const capLabel = storage.isUnlimited ? 'Unlimited' : formatBytes(storage.capBytes);

  if (compact) {
    return (
      <div
        className={cn(
          'rounded-2xl border border-foreground/10 bg-background/50 p-3 sm:p-4',
          className,
        )}
        role="region"
        aria-label="Storage usage"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-foreground/80">
            {storage.isUnlimited
              ? `${usedLabel} used · ${PLAN_LABELS[plan]}`
              : `${usedLabel} of ${capLabel} used`}
          </p>
          {!storage.isUnlimited && (
            <span className="shrink-0 text-[11px] tabular-nums text-foreground/50">
              {storage.percent}%
            </span>
          )}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
          <div
            className={cn('h-full rounded-full transition-all duration-500', barColor)}
            style={{ width: `${Math.max(storage.isUnlimited ? 4 : storage.percent, 1)}%` }}
          />
        </div>
        {storage.isAtLimit && !storage.isUnlimited && (
          <UpgradeNudgeCard
            reason="storage"
            currentPlan={plan}
            compact
            className="mt-3"
          />
        )}
      </div>
    );
  }

  // Full variant — used in Settings.
  return (
    <div
      className={cn(
        'space-y-4 rounded-2xl border border-foreground/10 bg-background p-5',
        className,
      )}
      role="region"
      aria-label="Storage usage"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="orbit-eyebrow text-lunar-teal">Storage</p>
          <p className="mt-1 text-sm font-medium text-foreground">
            {storage.isUnlimited
              ? 'Unlimited — reasonable use.'
              : `${usedLabel} of ${capLabel} used`}
          </p>
        </div>
        {!storage.isUnlimited && (
          <span className="shrink-0 text-xs tabular-nums text-foreground/60">
            {storage.percent}%
          </span>
        )}
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/10">
        <div
          className={cn('h-full rounded-full transition-all duration-500', barColor)}
          style={{ width: `${Math.max(storage.isUnlimited ? 8 : storage.percent, 1)}%` }}
        />
      </div>

      <StorageCopy plan={plan} storage={storage} />

      {storage.isOverLimit && !storage.isUnlimited && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(manageHref)}
            className="rounded-full"
          >
            Manage storage
          </Button>
          <Button
            size="sm"
            onClick={() => navigate(billingHref)}
            className="rounded-full bg-orbit-orange text-background hover:bg-orbit-orange/90"
          >
            View plans
          </Button>
        </div>
      )}

      {storage.isAtLimit && !storage.isOverLimit && !storage.isUnlimited && (
        <UpgradeNudgeCard reason="storage" currentPlan={plan} />
      )}
    </div>
  );
}

/**
 * The tier-specific paragraph beneath the bar. Split out so both the
 * `.tsx` in this file and downstream test snapshots can key on it.
 */
function StorageCopy({
  plan,
  storage,
}: {
  plan: SubscriptionPlan;
  storage: StorageBand;
}) {
  if (storage.isUnlimited) {
    return (
      <p className="text-xs text-foreground/60 leading-snug">
        Your Orbit Business plan includes unlimited storage for team
        content, delivery archives, and Nova-generated assets.
      </p>
    );
  }

  if (storage.isOverLimit) {
    return (
      <p className="text-sm text-foreground/85 leading-snug">
        You&apos;ve reached your Orbit storage limit. Your existing
        content is safe. To upload more, free up space or grow your
        plan.
      </p>
    );
  }

  if (storage.isAtLimit) {
    let upsell: string;
    if (plan === 'orbit_starter') upsell = 'Orbit adds 500 GB more.';
    else if (plan === 'orbit') upsell = 'Orbit Business adds unlimited storage.';
    else upsell = 'Grow your plan for more space.';
    return (
      <p className="text-sm text-orbit-orange leading-snug">
        More space is coming your way. {upsell}
      </p>
    );
  }

  if (storage.isNearLimit) {
    return (
      <p className="text-sm text-amber-600 dark:text-amber-400 leading-snug">
        You&apos;re getting close.
      </p>
    );
  }

  // <75% — no extra prose.
  return null;
}

export default StorageUsageBar;
